/**
 * Where ENHANCED TERRAIN's decoration gets its anchors — the measuring half of
 * `three/terrain-decoration.ts`, kept out of the renderer so it is testable as
 * plain arithmetic over real data.
 *
 * A sibling of `terrain-relief-source.ts` rather than part of it, because the
 * two answer different questions and only one of them is physics: this module's
 * output changes only what is *drawn*, and a wrong answer here is a picture
 * nobody likes rather than a duel that desynchronises.
 *
 * Two extractions, and the second is an interpretation — see
 * `three/terrain-decoration.ts`'s module doc for why reading the green
 * landcover entries as foliage ground is defensible and where it stops being a
 * decode.
 */

import { bestDrawableLod, type BakedModelBody } from "../three/baked-model.ts";
import {
  COPSE_RADIUS_M,
  COPSE_TREES,
  FOLIAGE_GROUND_COLOURS,
  woodSitesFrom,
  type FoliagePatch,
  type TreeSite,
} from "../three/terrain-decoration.ts";
import { placementWorldTransform, type PlacementOrigin } from "./placement-transform.ts";
import type { WorldRegionBody } from "./region-loader.ts";

/** A landcover plate is region-scale; anything smaller is a farm block or a building and is left alone. */
const MIN_PATCH_BOUNDING_RADIUS_M = 2000;

function packRgb(rgba: readonly number[]): number {
  return ((rgba[0] ?? 0) << 16) | ((rgba[1] ?? 0) << 8) | (rgba[2] ?? 0);
}

export interface TerrainFeatures {
  /** One per placement of the region's tree object — the only vegetation the data actually places. */
  readonly copseSites: readonly TreeSite[];
  /** Green landcover triangles, world XZ, for woods to be scattered inside. */
  readonly foliagePatches: readonly FoliagePatch[];
  /**
   * The woods themselves, derived from `foliagePatches` once.
   *
   * Held here rather than re-derived by each consumer because there are now
   * two — the trees (`three/terrain-decoration.ts`) and G20's canopy ground
   * paint (`three/wood-ground-paint.ts`) — and the paint's whole claim is that
   * it covers the ground the trees stand on. `woodSitesFrom` is deterministic,
   * so recomputing would give the same list today; deriving it once makes that
   * structural instead of a coincidence two lanes have to keep true.
   */
  readonly woodSites: readonly TreeSite[];
}

/**
 * Walks a region's placements once. `origin` must be the same recentring the
 * renderer uses, because both outputs are consumed in world space.
 */
export function terrainFeaturesFrom(
  region: WorldRegionBody,
  models: ReadonlyMap<string, BakedModelBody>,
  origin: PlacementOrigin,
): TerrainFeatures {
  const copseSites: TreeSite[] = [];
  const foliagePatches: FoliagePatch[] = [];

  for (const placement of region.placements) {
    const ref = placement.objectRef;
    if (ref === null) continue;
    const body = models.get(ref);
    if (!body) continue;
    const lod = bestDrawableLod(body.lods);
    if (!lod) continue;
    const { position, yawRadians } = placementWorldTransform(placement, origin);

    // A sphere node is the tree object and nothing else in any TERR library
    // (verified across all 289 baked TERR models: 5 carry spheres, one per
    // region, and they are the same object).
    if ((lod.spheres?.length ?? 0) > 0) {
      copseSites.push({ x: position.x, z: position.z, count: COPSE_TREES, radiusM: COPSE_RADIUS_M });
      continue;
    }

    if (body.boundingRadius < MIN_PATCH_BOUNDING_RADIUS_M) continue;
    const cos = Math.cos(yawRadians);
    const sin = Math.sin(yawRadians);
    const toWorld = (mx: number, my: number): [number, number] => {
      // The model frame's `y -> -Z` flip, then the placement's yaw and offset.
      const wx = mx;
      const wz = -my;
      return [cos * wx + sin * wz + position.x, -sin * wx + cos * wz + position.z];
    };

    for (let t = 0; t < lod.triangleCount; t++) {
      const face = lod.faceColors[t];
      if (!face || !FOLIAGE_GROUND_COLOURS.includes(packRgb(face.colorRgba))) continue;
      const corner = (k: number): [number, number] => {
        const v = lod.indices[t * 3 + k]!;
        return toWorld(lod.positions[v * 3]!, lod.positions[v * 3 + 1]!);
      };
      const [ax, az] = corner(0);
      const [bx, bz] = corner(1);
      const [cx, cz] = corner(2);
      foliagePatches.push({ ax, az, bx, bz, cx, cz });
    }
  }

  return { copseSites, foliagePatches, woodSites: woodSitesFrom(foliagePatches) };
}
